import { PostHogProvider, usePostHog } from "@posthog/react";
import { TanStackDevtools } from "@tanstack/react-devtools";
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  redirect,
  Scripts,
  useRouter,
} from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import type { TRPCOptionsProxy } from "@trpc/tanstack-react-query";
import type * as React from "react";
import { useEffect } from "react";

import { VIEWER_NAVIGATION_STALE_TIME } from "@/auth/shared";
import TanStackQueryDevtools from "@/integrations/tanstack-query/devtools";
import { Provider } from "@/integrations/tanstack-query/root-provider";
import type { TRPCRouter } from "@/integrations/trpc/router";
import { initVersionCheck } from "@/lib/version-check";

import appCss from "../styles.css?url";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

interface RouterContext {
  queryClient: QueryClient;
  trpc: TRPCOptionsProxy<TRPCRouter>;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context: { queryClient, trpc }, location }) => {
    const viewerQueryOpts = trpc.viewer.get.queryOptions();

    // Keep a reference to the previously cached viewer so we can detect
    // spurious null results caused by DB overload / timeouts.
    const previousViewer = queryClient.getQueryData(viewerQueryOpts.queryKey);

    let viewer: typeof previousViewer | null = null;
    try {
      viewer = await queryClient.ensureQueryData({
        ...viewerQueryOpts,
        staleTime: VIEWER_NAVIGATION_STALE_TIME,
        retry: false,
      });
    } catch {
      // Fetch failed — viewer stays null, handled below.
    }

    // If the user was previously logged in but the fetch failed or returned
    // null (transient failure, HMR, etc.), restore the cached viewer so
    // component hooks (staleTime: Infinity) don't see a spurious null.
    if (previousViewer && !viewer) {
      viewer = previousViewer;
      queryClient.setQueryData(viewerQueryOpts.queryKey, previousViewer);
    }

    if (
      !["/account/change-password", "/log-out"].includes(location.pathname) &&
      viewer?.needsPasswordChange
    ) {
      throw redirect({
        to: "/account/change-password",
      });
    }

    // Redirect verified users who haven't created a profile yet
    const setupExemptPaths = [
      "/account/setup",
      "/account/verify",
      "/account/change-password",
      "/log-out",
      "/log-in",
      "/sign-up",
    ];
    if (
      viewer?.needsProfileSetup &&
      !setupExemptPaths.some((p) => location.pathname.startsWith(p))
    ) {
      throw redirect({ to: "/account/setup" });
    }

    return {
      viewer,
    };
  },
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        title: "CBVA",
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
  }),
  shellComponent: RootDocument,
  component: function RootComponent() {
    useEffect(() => initVersionCheck(), []);

    const router = useRouter();
    const posthog = usePostHog();

    useEffect(() => {
      const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
      if (!measurementId) return;
      return router.subscribe("onResolved", (evt) => {
        if (evt.pathChanged) {
          window.gtag?.("event", "page_view", {
            page_path: evt.toLocation.pathname,
          });
        }
      });
    }, [router]);

    useEffect(() => {
      if (!import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN) return;
      return router.subscribe("onResolved", (evt) => {
        if (evt.pathChanged) {
          posthog.capture("$pageview", {
            $current_url: window.location.href,
          });
        }
      });
    }, [router, posthog]);

    return (
      <>
        <Outlet />
        <TanStackDevtools
          config={{
            position: "bottom-left",
          }}
          plugins={[
            TanStackQueryDevtools,
            {
              name: "Router",
              render: <TanStackRouterDevtoolsPanel />,
              defaultOpen: false,
            },
            // {
            // 	name: "Form",
            // 	render: <FormDevtoolsPanel />,
            // 	defaultOpen: false,
            // },
          ]}
        />
      </>
    );
  },
});

function RootDocument({ children }: { children: React.ReactNode }) {
  const { queryClient } = Route.useRouteContext();

  return (
    <html lang="en">
      <head>
        <HeadContent />
        {import.meta.env.VITE_GA_MEASUREMENT_ID && (
          <>
            <script
              async
              src={`https://www.googletagmanager.com/gtag/js?id=${import.meta.env.VITE_GA_MEASUREMENT_ID}`}
            />
            <script
              // biome-ignore lint/security/noDangerouslySetInnerHtml: gtag inline script
              dangerouslySetInnerHTML={{
                __html: `
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', '${import.meta.env.VITE_GA_MEASUREMENT_ID}');
                `,
              }}
            />
          </>
        )}
      </head>
      <body>
        {import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN ? (
          <PostHogProvider
            apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN}
            options={{
              // Routed through our own origin via nitro routeRules so
              // adblockers don't drop events. See vite.config.ts.
              api_host: "/ingest",
              ui_host: "https://us.posthog.com",
              defaults: "2025-05-24",
              capture_pageview: false,
              capture_exceptions: true,
              capture_performance: { web_vitals: true },
              session_recording: {
                maskAllInputs: true,
                maskTextSelector: "[data-private]",
              },
              disable_session_recording: import.meta.env.DEV,
              debug: import.meta.env.DEV,
            }}
          >
            <Provider queryClient={queryClient}>{children}</Provider>
          </PostHogProvider>
        ) : (
          <Provider queryClient={queryClient}>{children}</Provider>
        )}
        <Scripts />
      </body>
    </html>
  );
}
